import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Base_URL } from "../url";
import "../Styling/GoodsIssueReport.css";

/* ── tiny helpers ── */
const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const formatTime = (t, tz) => {
  if (!t) return "—";
  const [h, m] = t.split(":");
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const hour12 = ((hour + 11) % 12) + 1;
  return `${String(hour12).padStart(2, "0")}:${m} ${ampm} ${tz ?? ""}`.trim();
};

const statusClass = (status = "") => {
  const s = status.toLowerCase();
  if (s.includes("progress")) return "gir-badge--inprogress";
  if (s.includes("complet")) return "gir-badge--completed";
  return "gir-badge--pending";
};

/* ── SVG icons ── */
const IconBox = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>
);

const IconPrint = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 6 2 18 2 18 9" />
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
    <rect x="6" y="14" width="12" height="8" />
  </svg>
);

/* ════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════ */
export default function GoodsIssueReport() {
  const { productionOrderId } = useParams();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!productionOrderId) return;
    setLoading(true);
    setError(null);

    fetch(`${Base_URL}/ProductionOrder/GetGoodsIssueData/${productionOrderId}`)
      .then((res) => {
        if (!res.ok) throw new Error(`Server responded with ${res.status}`);
        return res.json();
      })
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [productionOrderId]);

  /* ── loading ── */
  if (loading) {
    return (
      <div className="gir-page">
        <div className="gir-state">
          <div className="gir-spinner" />
          <p className="gir-state-text">Fetching goods issue data…</p>
        </div>
      </div>
    );
  }

  /* ── error ── */
  if (error) {
    return (
      <div className="gir-page">
        <div className="gir-state">
          <div className="gir-error-box">⚠ {error}</div>
        </div>
      </div>
    );
  }

  /* ── derive header from first row ── */
  const header = data[0] ?? {};

  const totalChildQty = data.reduce((sum, r) => sum + (r.childQuantity ?? 0), 0);
  const totalParentQty = data.reduce((sum, r) => sum + (r.parentQty ?? 0), 0);

  return (
    <div className="gir-page">

      {/* ── Top bar ── */}
      <div className="gir-topbar">
        <div className="gir-topbar-brand">
          <div className="gir-topbar-icon"><IconBox /></div>
          <span className="gir-topbar-label">Production ERP</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span className="gir-topbar-badge">Goods Issue</span>
          <button className="gir-print-btn" onClick={() => window.print()}>
            <IconPrint /> Print
          </button>
        </div>
      </div>

      {/* ── Report card ── */}
      <div className="gir-card">

        {/* Title banner */}
        <div className="gir-title-banner">
          <div>
            <div className="gir-report-title">Goods Issue Report</div>
            <div className="gir-report-subtitle">{header.organisationName ?? "—"}</div>
          </div>
          <div className="gir-order-pill">{productionOrderId}</div>
        </div>

        <div className="gir-divider" />

        {/* ── SECTION 1 : Production Header Details ── */}
        <div className="gir-section">
          <div className="gir-section-title">Production Order Details</div>

          <div className="gir-info-grid">
            <div className="gir-info-cell">
              <div className="gir-info-label">Production Order ID</div>
              <div className="gir-info-value">{header.productionOrderId ?? "—"}</div>
            </div>

            <div className="gir-info-cell">
              <div className="gir-info-label">Production Type</div>
              <div className="gir-info-value">{header.productionTypeDescription ?? "—"}</div>
            </div>

            <div className="gir-info-cell">
              <div className="gir-info-label">Status</div>
              <div className="gir-info-value">
                <span className={`gir-badge ${statusClass(header.productionOrderStatusDescription)}`}>
                  {header.productionOrderStatusDescription ?? "—"}
                </span>
              </div>
            </div>

            <div className="gir-info-cell">
              <div className="gir-info-label">Organisation</div>
              <div className="gir-info-value">{header.organisationName ?? "—"}</div>
            </div>

            <div className="gir-info-cell">
              <div className="gir-info-label">Planned Start</div>
              <div className="gir-info-value">{formatDate(header.plannedStartDate)}</div>
              <div className="gir-item-sub">{formatTime(header.plannedStartTime, header.plannedStartTimezone)}</div>
            </div>

            <div className="gir-info-cell">
              <div className="gir-info-label">Planned End</div>
              <div className="gir-info-value">{formatDate(header.plannedEndDate)}</div>
              <div className="gir-item-sub">{formatTime(header.plannedEndTime, header.plannedEndTimezone)}</div>
            </div>

            <div className="gir-info-cell">
              <div className="gir-info-label">Approx. Duration</div>
              <div className="gir-info-value">{header.approxDuration != null ? `${header.approxDuration} hr` : "—"}</div>
            </div>

            <div className="gir-info-cell">
              <div className="gir-info-label">Approx. Cost</div>
              <div className="gir-info-value">
                {header.approxCost != null
                  ? `${header.approxCostCurrency} ${Number(header.approxCost).toLocaleString("en-IN")}`
                  : "—"}
              </div>
            </div>
          </div>
        </div>

        <div className="gir-separator" />

        {/* ── SECTION 2 : Items ── */}
        <div className="gir-section">
          <div className="gir-section-title">Bill of Materials — Goods Issue Items</div>
        </div>

        <div className="gir-table-wrapper">
          <table className="gir-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Parent Item</th>
                <th>Parent Qty</th>
                 <th>Qty / Parent</th>
                <th>Component Item</th>
                <th>Required Qty</th>
               
              </tr>
            </thead>
            <tbody>
              {data.map((row, idx) => (
                <tr key={idx}>
                  <td>
                    <div className="gir-row-index">{idx + 1}</div>
                  </td>
                  <td>
                    <div className="gir-item-name">{row.parentItem ?? "—"}</div>
                  </td>
                  <td>
                    <span className="gir-qty-chip">{row.parentQty ?? 0}</span>
                  </td>
                     <td>
                    <span className="gir-ratio-chip"> {row.qtyPerParent ?? 1}</span>
                  </td>
                  <td>
                    <div className="gir-item-name">{row.childItem ?? "—"}</div>
                  </td>
                  <td>
                    <span className="gir-qty-chip">{row.childQuantity ?? 0}</span>
                  </td>
               
                </tr>
              ))}
            </tbody>
          </table>

          {/* footer totals */}
          <div className="gir-table-footer">
            <div>
              <div className="gir-total-label">Total Items</div>
              <div className="gir-total-value">{data.length}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div className="gir-total-label">Total Parent Qty &nbsp;|&nbsp; Total Child Qty</div>
              <div className="gir-total-value">{totalParentQty} &nbsp;|&nbsp; {totalChildQty}</div>
            </div>
          </div>

          {/* cost accent */}
          {header.approxCost != null && (
            <div className="gir-cost-banner">
              <span className="gir-cost-banner-label">Estimated Production Cost</span>
              <span className="gir-cost-banner-value">
                {header.approxCostCurrency} {Number(header.approxCost).toLocaleString("en-IN")}
              </span>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}